import { useTranslation } from "@i18n";
import React, { forwardRef, useEffect, useRef, useState, MutableRefObject, FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import { convertDate } from "@Services/luxonHelper";
import SlickGrid1 from "@CubeComponents/html/SlickGrid1";
import { MdEdit } from "react-icons/md";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { useDispatch, useSelector } from "react-redux";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_SUCCESS } from "@Services/constants";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import DeleteIcon from "@Components/StickySidebarViewer/assets/DeleteIcon";
import _ from "lodash";
import ChecklistProperties from "./ChecklistProperties.jsx";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent.js";
import { manageChecklist } from "@Features/reviews/helpers";
import { portalCreateRoot as createRoot } from "@CubeComponents/html/cellPortals";

import { renderUserTag } from "@CubeComponents/html/userTagDom";
import ToolTipComponent from "@CubeComponents/html/tooltip/index.js";
import { recordStatus } from "helpers/constants";
import IconComponent from "@CubeComponents/icons";
import { getChecklistDetailUrl } from "@Services/utils/helpers.js";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import { getBackendUrl } from "@Utils";
import { GridPageLayout } from "@CubeComponents/html/GridPageLayout";
import type { RootState } from "@Reducers/index";

type CheckListDataProps = {
  setIsRowSelected?: (value: boolean) => void;
  showOnlyGrid?: boolean;
  customQuery?: any;
  selectedProjectIds?: Array<string | number>;
};

const CheckListData = forwardRef<any, CheckListDataProps>(({ setIsRowSelected, showOnlyGrid = false }, ref) => {
  const { t } = useTranslation();
  const localRef = useRef<any>(null);
  const dataGrid: MutableRefObject<any> = (ref as any) || localRef;
  const [hasSelection, setHasSelection] = useState(false);
  const [checklistsQuery, setChecklistsQuery] = useState<any>(null);
  const [isLoadingChecklist, setIsLoadingChecklist] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [isSubmittingEdit, setIsSubmittingEdit] = useState(false);

  const [modalState, setModalState] = useState<any>({
    show: false,
    type: null,
    details: {},
  });
  const [createTitle, setCreateTitle] = useState("");
  const [createDescription, setCreateDescription] = useState("");
  const [isSubmittingCreate, setIsSubmittingCreate] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [recordProperties, setRecordProperties] = useState<any>({
    show: false,
    details: {},
  });

  const [selectedItems, setSelectedItems] = useState<any[]>([]);

  const { portalSettingInfo } = useSelector((state: RootState) => state);

  const handlePropertiesOpen = () => {
    const selected = dataGrid.current?.dataView?.getAllSelectedItems?.() ?? [];
    setSelectedItems(selected);
    setRecordProperties((prev: any) => ({
      show: !prev.show,
      details: selected.length === 1 ? selected[0] : {},
    }));
  };

  useEffect(() => {
    const initialQuery = buildBaseQuery();
    setChecklistsQuery(initialQuery);
  }, []);

  const buildBaseQuery = () => {
    const params: any = {};
    return { params };
  };

  const openModal = (type: string, details: any = {}) => {
    setModalState({ show: true, type, details });
    if (type === "create") {
      setCreateTitle("");
      setCreateDescription("");
      setIsSubmittingCreate(false);
    } else if (type === "edit") {
      setEditTitle(details.title || "");
      setEditDescription(details.description || "");
      setIsSubmittingEdit(false);
    }
  };

  const closeModal = () => setModalState({ show: false, type: null, details: {} });

  const handleEditOpen = () => {
    const selected = dataGrid.current.dataView.getAllSelectedItems();
    if (selected.length === 1) openModal("edit", selected[0]);
  };

  const handleDeleteClick = () => {
    const selected = dataGrid.current.dataView.getAllSelectedItems();
    if (selected.length >= 1) openModal("delete", selected);
  };

  const performDeleteOperation = async () => {
    setIsLoadingChecklist(true);
    const selectedIds = modalState.details.map((item: any) => item._id);
    const data = {
      selectedIds,
      recordData: recordStatus.DELETED,
    };
    const response = await manageChecklist(data);
    dataGrid.current.refresh();
    if (response) {
      dispatch(
        showToastNotification({
          content: t("The selected checklist(s) have been deleted successfully."),
          type: TOAST_TYPE_SUCCESS,
        }) as any,
      );
    }
    setIsLoadingChecklist(false);
    closeModal();
  };

  const handleSubmit = async (e?: FormEvent) => {
    e?.preventDefault();

    if (modalState.type === "edit") {
      if (!editTitle.trim()) return;
      const selectedId = modalState.details?._id;
      setIsSubmittingEdit(true);
      try {
        const data = {
          _id: selectedId,
          title: editTitle.trim(),
          description: editDescription.trim(),
        };
        const response = await manageChecklist(data);
        dataGrid.current?.refresh?.();

        if (response.data?.status) {
          dispatch(
            showToastNotification({
              content: t("The changes have been made successfully!"),
              type: TOAST_TYPE_SUCCESS,
            }) as any,
          );
        }
      } finally {
        setIsSubmittingEdit(false);
      }
    } else {
      if (!createTitle.trim()) return;
      setIsSubmittingCreate(true);
      try {
        const data = {
          title: createTitle.trim(),
          description: createDescription.trim(),
          allowMultiple: false,
          choices: [{ value: "Yes" }, { value: "No" }, { value: "N/A" }],
        };
        const response = await manageChecklist(data);
        dataGrid.current?.refresh?.();

        if (response.data?.status) {
          dispatch(
            showToastNotification({
              content: t("The checklist has been created successfully!"),
              type: TOAST_TYPE_SUCCESS,
            }) as any,
          );
        }
      } finally {
        setIsSubmittingCreate(false);
      }
    }
    closeModal();
  };

  const createBtnTemplate = () => (
    <ButtonComponent
      startIcon={<AddRoundedIcon />}
      onClick={() => openModal("create")}
      className="px-4 py-1.5 rounded-md bg-gray-150 text-gray-800 hover:bg-gray-200 text-sm font-medium ms-2"
    >
      {t("Create")}
    </ButtonComponent>
  );

  const editBtnTemplate = () => (
    <ButtonComponent buttonType={BUTTON_TYPES.TEXT_BUTTON} onClick={handleEditOpen}>
      <div className="flex items-center gap-1">
        <MdEdit size={16} />
        <span>{t("Edit")}</span>
      </div>
    </ButtonComponent>
  );

  const deleteBtnTemplate = () => (
    <ButtonComponent buttonType={BUTTON_TYPES.TEXT_BUTTON} onClick={handleDeleteClick}>
      <div className="flex items-center gap-1">
        <DeleteIcon size={16} className={""} />
        <span>{t("Delete")}</span>
      </div>
    </ButtonComponent>
  );

  const toolbarForChecklists = [
    {
      id: "create_checklist",
      template: createBtnTemplate,
      disabled: !portalSettingInfo?.reviewFlowsChecklist?.addReviewFlowsChecklist?.enable,
    },
    {
      id: "edit_checklist",
      template: editBtnTemplate,
      disabled: !portalSettingInfo?.reviewFlowsChecklist?.editReviewFlowsChecklist?.enable || !hasSelection,
    },
    {
      id: "delete_checklist",
      template: deleteBtnTemplate,
      disabled: !portalSettingInfo?.reviewFlowsChecklist?.deleteReviewFlowsChecklist?.enable || !hasSelection,
    },
  ];

  const creatorTemplate = (args: any) => {
    const container = document.createElement("div");
    renderUserTag(container, { id: args.createdBy?.userId });
    return container;
  };

  const createdDateTemplate = (args: any) => {
    if (!args.createdDate) return "-";
    const dateFormat = convertDate(Number(args.createdDate), "dateForTooltip");
    const container = document.createElement("div");
    const root = createRoot(container);
    root.render(
      <ToolTipComponent position="bottom" content={dateFormat}>
        <span>{convertDate(Number(args.createdDate), "dateForText")}</span>
      </ToolTipComponent>,
    );
    return container;
  };

  const [checklistGridColumns] = useState<any[]>([
    { id: "title", name: t("Title"), field: "title", filterType: "Text" },
    { id: "description", name: t("Description"), field: "description", filterType: "Text" },
    {
      id: "createdBy",
      name: t("Created By"),
      field: "createdBy",
      filterType: "Text",
      formatter: (data: number, params: number, a: any, b: unknown, value: any) => creatorTemplate(value),
      _valueGetter: (params: any) => `${params.createdBy.firstName} ${params.createdBy.lastName}`,
    },
    {
      id: "createdDate",
      name: t("Created On"),
      field: "createdDate",
      filterType: "Date",
      formatter: (data: number, params: number, a: any, b: unknown, value: any) => createdDateTemplate(value),
      _valueGetter: (params: any) => convertDate(Number(params.createdDate), "dateForTooltip"),
    },
  ]);

  const modalHeader = () => {
    const titles: any = {
      create: t("Create Checklist"),
      edit: t("Edit Checklist"),
      delete: t("Delete Checklist(s)"),
    };
    return (
      <div className="text-2xl font-semibold leading-[120%] text-[var(--Text-text-primary,#192048)]">
        {titles[modalState.type]}
      </div>
    );
  };

  const modalContent = () => {
    switch (modalState.type) {
      case "create":
      case "edit": {
        const isEdit = modalState.type === "edit";
        const titleValue = isEdit ? editTitle : createTitle;
        const descriptionValue = isEdit ? editDescription : createDescription;
        return (
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-[10rem_1fr] gap-4 items-start mb-5">
              <label
                id="checklist-title-label"
                htmlFor="checklist-title"
                className="overflow-hidden text-ellipsis whitespace-nowrap text-base font-semibold leading-[120%] text-[var(--Text-text-primary,#192048)] pt-2"
              >
                {t("Title")}
              </label>
              <div className="flex flex-col gap-1">
                <TextFieldComponent
                  id="checklist-title"
                  aria-labelledby="checklist-title-label"
                  placeholder={t("Enter Title")}
                  value={titleValue}
                  inputProps={{
                    maxLength: 70,
                  }}
                  onChange={(e) => (isEdit ? setEditTitle(e.target.value) : setCreateTitle(e.target.value))}
                  size="small"
                />
                {titleValue.length >= 70 && (
                  <span className="text-xs text-red-600">
                    {t("Please don't enter more than {{max, number}} characters", { max: 70 })}
                  </span>
                )}
              </div>
            </div>

            <div className="grid grid-cols-[10rem_1fr] gap-4 items-start">
              <label
                id="checklist-description-label"
                htmlFor="checklist-description"
                className="overflow-hidden text-ellipsis whitespace-nowrap text-base font-semibold leading-[120%] text-[var(--Text-text-primary,#192048)] pt-2"
              >
                {t("Description")}
              </label>
              <div className="flex flex-col gap-1">
                <TextFieldComponent
                  id="checklist-description"
                  aria-labelledby="checklist-description-label"
                  placeholder={t("Enter Description")}
                  value={descriptionValue}
                  slotProps={{
                    htmlInput: {
                      maxLength: 100,
                    },
                  }}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    isEdit ? setEditDescription(e.target.value) : setCreateDescription(e.target.value)
                  }
                  size="small"
                  multiline
                  minRows={6}
                />
                {descriptionValue.length >= 100 && (
                  <span className="text-xs text-red-600">
                    {t("Please don't enter more than {{max, number}} characters", { max: 100 })}
                  </span>
                )}
              </div>
            </div>
          </form>
        );
      }

      case "delete":
        return <div>{t("Are you sure you want to delete the selected checklist(s)?")}</div>;

      default:
        return null;
    }
  };

  const modalFooter = () => {
    switch (modalState.type) {
      case "create":
        return (
          <div className="flex gap-4">
            <ButtonComponent
              buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
              onClick={handleSubmit}
              loading={isSubmittingCreate}
              disabled={!createTitle.trim() || isSubmittingCreate}
            >
              {t("Create")}
            </ButtonComponent>
            <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={closeModal}>
              {t("Cancel")}
            </ButtonComponent>
          </div>
        );
      case "edit":
        return (
          <div className="flex gap-4">
            <ButtonComponent
              buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
              onClick={handleSubmit}
              loading={isSubmittingEdit}
              disabled={!editTitle.trim() || isSubmittingEdit}
            >
              {t("Save Changes")}
            </ButtonComponent>
            <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={closeModal}>
              {t("Cancel")}
            </ButtonComponent>
          </div>
        );
      case "delete":
        return (
          <div className="flex gap-4">
            <ButtonComponent
              buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
              onClick={performDeleteOperation}
              loading={isLoadingChecklist}
            >
              {t("Delete")}
            </ButtonComponent>
            <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={closeModal}>
              {t("Cancel")}
            </ButtonComponent>
          </div>
        );
      default:
        return null;
    }
  };

  const handleSelectedRowIdsChanged = () => {
    if (!_.get(dataGrid, "current.dataView")) return;
    const selected = dataGrid.current.dataView.getAllSelectedItems();
    const selectedCount = selected.length;
    setHasSelection(selectedCount > 0);
    if (setIsRowSelected) setIsRowSelected(selectedCount > 0);
    setSelectedItems(selected);
    setRecordProperties((prev: any) => ({
      ...prev,
      details: selectedCount === 1 ? selected[0] : {},
    }));
  };

  const placeholderPanel = (
    <aside className="flex w-[27.6875rem] h-full items-center justify-center bg-white border-s border-gray-200 rounded-md shadow-sm">
      <div className="flex flex-col items-center text-center gap-2 px-6">
        <IconComponent name={"NoData"} className="cursor-pointer" width="20rem" height="20rem" />
        <p className="text-2xl font-medium text-gray-600">
          {t(selectedItems.length > 1 ? "Multiple Checklists selected" : "No Checklist selected")}
        </p>
        <p className="text-s text-[var(--Text-text-secondary,#667085)]">
          {t(
            selectedItems.length > 1
              ? "To view properties, select one."
              : "To view properties, you need to select one.",
          )}
        </p>
      </div>
    </aside>
  );

  const handleRecordClick = (args: any) => {
    if (args.detail.args.cell === 0) return;
    const rowData = dataGrid.current.slickGrid.getDataItem(args.detail.args.row);
    navigate(getChecklistDetailUrl(rowData._id));
  };

  return (
    <div className="flex gap-4 h-full w-full">
      <GridPageLayout title="Reviews" description="Manage all settings related to Reviews module">
        <SlickGrid1
          ref={dataGrid}
          remoteDataSource={`${getBackendUrl()}/reviews/getChecklists`}
          query={checklistsQuery}
          columnDefinitions={checklistGridColumns}
          toolbar={(showOnlyGrid ? null : toolbarForChecklists) as any}
          infiniteScroll
          enableFilteringOption
          enableSortingOption
          propertiesPanel={!showOnlyGrid}
          toolbarClickHandler={(id: string) => {
            if (id === "properties") {
              handlePropertiesOpen();
            }
          }}
          onSelectedRowIdsChanged={handleSelectedRowIdsChanged}
          onClick={handleRecordClick}
        />
      </GridPageLayout>

      {recordProperties.show &&
        (selectedItems.length === 1 ? (
          <ChecklistProperties checklist={selectedItems[0]} _id={selectedItems[0]?._id} dataGrid={dataGrid} />
        ) : (
          placeholderPanel
        ))}

      {modalState.show && (
        <ModalStateDialogComponent
          isVisible={modalState.show}
          allowModalPadding={false}
          width="50rem"
          headerTitle={modalHeader() || undefined}
          modelContent={modalContent() || undefined}
          onCancel={closeModal}
          footerContent={modalFooter() || undefined}
        />
      )}
    </div>
  );
});

CheckListData.displayName = "CheckListData";

export default CheckListData;
